import React, { useEffect, useMemo, useState } from 'react'
import { OnPremResponseProps, ResourceRequestsProps, webURLResponseProps } from '../resourceRequestInterface'
import { myLocalStorage } from '../../../StorageHelper';
import useUserStore from '../../../../services/userStore';
import { AlertTriangle, Calendar, CheckCircle2, Clock, Eye, Globe, MessageSquare, RotateCcw, User, XCircle } from 'lucide-react';
import { getWebURLNotificationAPI, getWebURLNotificationCountAPI, revokeRequest, updateRequest } from '../Api/resourcerequestapi';
import { format } from "date-fns";
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { getStatusColor } from '../utils';
import { Skeleton } from '../../interfaces/CommonInterfaces';
import { formatDate } from '../../formatTimeAgo';
import { Badge } from '../../../ui/badge';


export const WebUrlRequestDetails = ({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  startDate,
  endDate,
  activeTab
}: ResourceRequestsProps) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<webURLResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<webURLResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );
  const { email, role, userRole } = useUserStore((state) => state.user);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const handleViewDetails = (request: webURLResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
  };

  const handleRequest = async (status: string, request: webURLResponseProps) => {
    setLoadingId(request.id)
    let payload = {
      accessId: request.id,
      requestStatus: status,
      accessType: request.accessType,
    }
    await updateRequest(tenantName, payload)
    fetchWebNotification(false);
    setLoadingId(null)
  }


  const handleRevoke = async (accessId: number) => {
    await revokeRequest("weburl", accessId)
    fetchWebNotification(false);
  }

  const fetchWebURLCount = async () => {
    if (userRole === "epam-admin") return;

    const response = await getWebURLNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role
    )
    setTotalItems(response || 0);
  }

  const fetchWebNotification = async (isFirst = true) => {
    if (userRole === "epam-admin") return;
    setIsLoading(isFirst);
    try {
      const response: webURLResponseProps[] = await getWebURLNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchWebURLCount();
    fetchWebNotification();
    if (activeTab === "weburl") {
      const interval = setInterval(() => {
        fetchWebURLCount();
        fetchWebNotification(false);
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-3 h-3" />;
      case "APPROVED":
        return <CheckCircle2 className="w-3 h-3" />;
      case "DECLINED":
        return <XCircle className="w-3 h-3" />;
      case "REVOKED":
        return <RotateCcw className="w-3 h-3" />;
      case "EXPIRED":
        return <AlertTriangle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <div className="space-y-1">
      {isLoading ?
        <>
          {[...Array(3)].map((_, idx) => (
            <Card key={idx} className="border-slate-200 dark:border-slate-700">
              <CardContent className="p-4 animate-pulse">
                <div className="flex justify-between items-center mb-3">
                  <Skeleton className="h-4 w-32 rounded" />
                  <Skeleton className="h-6 w-20 rounded" />
                </div>
                <Skeleton className="h-3 w-full rounded mb-2" />
                <Skeleton className="h-3 w-3/4 rounded mb-2" />
                <Skeleton className="h-3 w-1/2 rounded" />
                <div className="mt-4 flex justify-between">
                  <Skeleton className="h-6 w-28 rounded" />
                  <Skeleton className="h-6 w-28 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}

        </> : notifications.length > 0 ? (
          <>

            {/* Request Cards */}
            {notifications.map((request) => (
              <Card
                key={request.id}
                className="transition-all duration-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                data-testid={`weburl-request-card-${request.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">

                    <div className="flex-1 space-y-1">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5 text-indigo-500" />
                            <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
                              {new URL(request.accessUrl).hostname}
                            </h3>
                            <Badge className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}>
                              <div className="flex items-center gap-1">
                                {getStatusIcon(request.requestStatus)}
                                {request.requestStatus}
                              </div>
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(request.createdAt)}
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {request.email}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                          className="h-auto px-2 py-1 text-xs"
                          data-testid={`button-details-weburl-${request.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      </div>

                      {/* Content */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">URL:</span>
                          <div className="font-medium truncate text-slate-700 dark:text-slate-200">{request.accessUrl}</div>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">Credential:</span>
                          <div className="font-medium text-slate-700 dark:text-slate-200">{request.accessCredential}</div>
                        </div>



                        {request.privilegeLevel === "PERMANENT" ? <div>
                          <span className="text-slate-500 dark:text-slate-400">Access Type</span>
                          <div className="font-medium text-slate-700 dark:text-slate-200">Permanent</div>
                        </div> : <div>
                          <span className="text-slate-500 dark:text-slate-400">Privilege:</span>
                          <div className="font-medium text-slate-700 dark:text-slate-200">{request.privilegeLevel}</div>
                        </div>}

                        {request.schedules.length === 0 && request.selectedTime !== 0 && <div>
                          <span className="text-slate-500 dark:text-slate-400">Duration:</span>
                          <div className="font-medium text-slate-700 dark:text-slate-200">
                            {request.privilegeLevel === "PERMANENT" ? "Permanent" : `${request.selectedTime} min`}


                          </div>
                        </div>}


                      </div>

                      {request.schedules?.length > 0 && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Scheduled Access</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <label className="text-blue-600 dark:text-blue-400 font-medium">Start Time</label>
                              <p className="text-blue-800 dark:text-blue-200">{formatDate(request.schedules[0].scheduledStartTime)}</p>
                            </div>
                            <div>
                              <label className="text-blue-600 dark:text-blue-400 font-medium">End Time</label>
                              <p className="text-blue-800 dark:text-blue-200">{formatDate(request.schedules[0].scheduledEndTime)}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Admin Actions History */}
                      {((request.approvalEmailId?.length ?? 0) > 0 || request.revokedBy) && (
                        <div className="p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                          <div className="mb-2 text-xs font-medium text-amber-600 dark:text-amber-400">Admin Actions</div>
                          <div className="space-y-1 text-sm">
                            {request.approvalEmailId.length > 0 && (
                              <p className="text-green-700 dark:text-green-300">
                                ✓ Approved by {request.approvalEmailId[0]} on {formatDate(request.createdAt)}
                              </p>
                            )}
                            {request.revokedBy && (
                              <p className="text-purple-700 dark:text-purple-300">
                                ⊝ Revoked by {request.revokedBy}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <MessageSquare className="w-3 h-3" />
                          {request.requestMessage}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Conditional Action Buttons */}
                          {request.requestStatus === "PENDING" && (
                            <>
                              {role !== "TENANT_USER" ? (
                                <>
                                  <Button
                                    disabled={loadingId === request.id}

                                    size="sm"
                                    onClick={() => handleRequest("APPROVED", request)}
                                    className="text-white bg-green-600 hover:bg-green-700"
                                    data-testid={`button-approve-weburl-${request.id}`}
                                  >
                                    {loadingId === request.id ? (
                                      <>
                                        <svg
                                          className="animate-spin w-4 h-4 mr-1 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                          ></path>
                                        </svg>
                                        Approving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRequest("DECLINE", request)}
                                    data-testid={`button-decline-weburl-${request.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Decline
                                  </Button>
                                </>) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                                    onClick={() => setIsDetailsOpen?.(false)}
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                    onClick={() => { handleRevoke(request.id) }}>
                                    <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                                  </Button>
                                </>
                              )}
                            </>
                          )}

                          {request.requestStatus === "APPROVED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { handleRevoke(request.id) }}
                              className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                              data-testid={`button-revoke-weburl-${request.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

          </>
        ) : (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-12 text-center">
              <Globe className="w-16 h-16 mx-auto mb-6 text-indigo-300 dark:text-indigo-600" />
              <h3 className="mb-3 text-xl font-semibold text-slate-700 dark:text-slate-200">
                No WebURL Requests Found
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                No web application access requests match your current filters.
              </p>
            </CardContent>
          </Card>
        )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Web Application Access Request Details</DialogTitle>
            <DialogDescription>
              Complete details for the web application access request
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Request ID:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.id}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Status:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.requestStatus}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Requested By:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.email}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Created:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.createdAt}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Credential:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.accessCredential}</p>
                </div>

              </div>
              <div>
                <span className="font-medium text-slate-600 dark:text-slate-300">Web URL:</span>
                <p className="mt-1 font-mono text-xs text-slate-800 dark:text-slate-100">{selectedRequest.accessUrl}</p>
              </div>
              <div>
                <span className="font-medium text-slate-600 dark:text-slate-300">Justification:</span>
                <p className="text-slate-800 dark:text-slate-100">{selectedRequest.requestMessage}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  )
}
